/**
 * URLs for the Laravel file service, which serves every file the apps display.
 *
 * Two routes, and the difference between them is the only thing a caller has to get right:
 *
 * - `GET /files/public/{fileUuid}` — no credentials. Anything reachable without a session goes
 *   here: checklist covers on the marketing sites, the public checklist pages, server-rendered
 *   Next.js pages that have no Clerk session to sign with.
 * - `GET /files/{fileUuid}` — a Clerk token, or a 401. Everything inside an account.
 *
 * Both take a **uuid**. The old `mdcApiHost` file service keyed off the numeric `files.laravel_id`
 * and that is the migration's one breaking difference: `files.id` in the local mirror is already
 * the uuid these want, while `laravel_id` is the number the old service wanted. Passing a
 * `laravel_id` here does not 404 in a way you will notice quickly — it 500s out of the model
 * lookup — so it is worth checking which column a call site actually reads.
 *
 * Everything here is a pure string function so the Next.js apps can build these URLs on the server,
 * where there are no hooks. The React entry points live in `./hooks/useFileImageUrls`.
 */

/** A rendition the file service can produce. `base` is the original upload, at full size. */
export type FileImageFormat =
  | 'base'
  | 'card'
  | 'cardLg'
  | 'cardSm'
  | 'thumbnail'
  | 'thumbnailLg'
  | 'thumbnailSm'
  | 'cover'
  | 'coverSm';

/** One URL per rendition. Structurally the same shape as the old service's `ImageUrlFormats`. */
export type FileImageUrls = Partial<Record<FileImageFormat, string>>;

/**
 * An image ready to hand to `expo-image`'s `source`.
 *
 * Typed structurally rather than imported from `expo-image`, because this package is shared with
 * the web apps and must not depend on it.
 *
 * `cacheKey` is the point of the type. A private URL carries a Clerk token that rotates hourly, and
 * every image cache — `expo-image` included — keys off the URI, so a token refresh would otherwise
 * re-download and re-decode a picture the device already holds. The key is built from the file uuid
 * and the format instead, which is what actually identifies the bytes.
 *
 * `headers` is set on private sources only, and carries the same token as the query string does.
 * React Native can authenticate an image request by header; a browser's `<img src>` cannot, which
 * is why the token is in the URL as well.
 */
export type FileImageSource = {
  uri: string;
  cacheKey: string;
  headers?: Record<string, string>;
};

export type FileImageSources = Partial<Record<FileImageFormat, FileImageSource>>;

/**
 * The `?format=` the service renders for each rendition.
 *
 * `base` has none — omitting the parameter is what asks for the original. An unrecognised value is
 * *not* an error: the service quietly serves the original, so a typo here degrades to a full-size
 * image rather than a broken one, and only shows up as a fat download.
 */
const FILE_IMAGE_FORMAT_PARAMS: Record<FileImageFormat, string | null> = {
  base: null,
  card: 'card',
  cardLg: 'card-lg',
  cardSm: 'card-sm',
  thumbnail: 'thumbnail',
  thumbnailLg: 'thumbnail-lg',
  thumbnailSm: 'thumbnail-sm',
  cover: 'cover',
  coverSm: 'cover-sm',
};

export const FILE_IMAGE_FORMATS = Object.keys(FILE_IMAGE_FORMAT_PARAMS) as FileImageFormat[];

/** The Clerk JWT template the file service accepts. Lives an hour. */
export const FILE_TOKEN_TEMPLATE = 'one_hour_token';

/**
 * The query parameter the private route reads the Clerk token from.
 *
 * A query parameter and not only the `Authorization` header because the consumer of these URLs is
 * an `<img>` or an `expo-image`, neither of which lets the caller set a header on the browser's
 * behalf. Named here so that if the backend ever moves it, this is the line that changes.
 */
export const FILE_TOKEN_QUERY_PARAM = 'token';

/** Rendition controls the service accepts alongside `format`, per the OpenAPI spec. */
export type FileImageOptions = {
  /** Width in pixels. */
  w?: number;
  /** Height in pixels. */
  h?: number;
  /** Quality, 1–100. */
  q?: number;
};

/** `{apiUrl}/files/public/{uuid}`, unsigned — the route that needs no session. */
export const buildPublicFileUrl = (apiUrl: string, fileUuid: string): string =>
  `${apiUrl}/files/public/${encodeURIComponent(fileUuid)}`;

/** `{apiUrl}/files/{uuid}`, unsigned — sign it with {@link signPrivateFileUrl} before use. */
export const buildPrivateFileUrl = (apiUrl: string, fileUuid: string): string =>
  `${apiUrl}/files/${encodeURIComponent(fileUuid)}`;

/**
 * `url` with `params` appended, joining with `?` or `&` depending on what is already there.
 *
 * The join is the whole reason this exists. The predecessor hooks each hard-coded one or the other
 * — the public one always `?format=`, the private one always `&format=` after its token — so
 * neither could add a second parameter without producing a URL the service reads as one long
 * malformed value.
 */
const appendQuery = (url: string, params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }

  const query = search.toString();

  if (!query) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
};

/** A file URL with the Clerk token attached, for the private route. */
export const signPrivateFileUrl = (fileUrl: string, token: string): string =>
  appendQuery(fileUrl, { [FILE_TOKEN_QUERY_PARAM]: token });

/**
 * Every rendition of one file URL.
 *
 * `fileUrl` may already carry a token — the query join handles either case — so this composes with
 * {@link signPrivateFileUrl} in whichever order reads better at the call site.
 */
export const buildFileImageUrls = (fileUrl: string, options: FileImageOptions = {}): FileImageUrls =>
  FILE_IMAGE_FORMATS.reduce<FileImageUrls>((urls, format) => {
    urls[format] = appendQuery(fileUrl, { ...options, format: FILE_IMAGE_FORMAT_PARAMS[format] ?? undefined });
    return urls;
  }, {});

/**
 * The same URLs paired with a token-free cache key, so an hourly token refresh doesn't invalidate
 * pictures the device has already downloaded and decoded.
 */
export const buildFileImageSources = (
  urls: FileImageUrls,
  fileUuid: string,
  headers?: Record<string, string>,
): FileImageSources =>
  FILE_IMAGE_FORMATS.reduce<FileImageSources>((sources, format) => {
    const uri = urls[format];

    if (uri) {
      sources[format] = { uri, cacheKey: `mdc-file-${fileUuid}-${format}`, ...(headers && { headers }) };
    }

    return sources;
  }, {});

/**
 * Every rendition of a **public** file, with no session involved.
 *
 * The one to reach for outside React: `getStaticProps`, a Next.js server component, an `<Image>`
 * rendered from a checklist the visitor has not signed in to see. Returns `undefined` for a missing
 * uuid so a caller can pass a nullable field straight through.
 */
export const buildPublicFileImageUrls = (
  apiUrl: string | undefined,
  fileUuid: string | null | undefined,
  options: FileImageOptions = {},
): FileImageUrls | undefined => {
  if (!apiUrl || !fileUuid) {
    return undefined;
  }

  return buildFileImageUrls(buildPublicFileUrl(apiUrl, fileUuid), options);
};

/**
 * Every rendition of a **private** file, signed with an already-resolved Clerk token.
 *
 * Getting that token is the caller's problem, which is what keeps this function pure — inside React
 * use `usePrivateFileImageUrls`, which shares one token across the whole app.
 */
export const buildPrivateFileImageUrls = (
  apiUrl: string | undefined,
  fileUuid: string | null | undefined,
  token: string | null | undefined,
  options: FileImageOptions = {},
): FileImageUrls | undefined => {
  if (!apiUrl || !fileUuid || !token) {
    return undefined;
  }

  return buildFileImageUrls(signPrivateFileUrl(buildPrivateFileUrl(apiUrl, fileUuid), token), options);
};
